import { inject, Injectable, Type } from '@angular/core';
import { DialogService as PrimeDialogService } from 'primeng/dynamicdialog';
import {
    EMPTY,
    filter,
    finalize,
    map,
    Observable,
    race,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { CustomDialog } from '../../models/custom-dialog/custom-dialog.model';
import { DialogSizeEnum } from 'shared/design-system/data-access/dialog';

@Injectable({
    providedIn: 'root',
})
export class DialogService {
    #dialogService = inject(PrimeDialogService);

    openDialog = (inputs: ConfirmDialogComponent['inputs']): Observable<void> =>
        this.openCustomDialog(
            ConfirmDialogComponent,
            inputs,
            DialogSizeEnum.SMALL,
        );

    openCustomDialog = <Output, Input>(
        component: Type<CustomDialog<Output, Input>>,
        inputs: Input,
        width: DialogSizeEnum,
        options?: { dismissable?: boolean },
    ): Observable<Output> => {
        const dismissable = options?.dismissable ?? true;
        const ref = this.#dialogService.open(component, {
            showHeader: false,
            modal: true,
            dismissableMask: dismissable,
            closeOnEscape: dismissable,
            width: width,
            contentStyle: {
                display: 'flex',
                flexDirection: 'column',
                padding: 0,
                minWidth: 0,
                maxHeight: 'calc(100vh - 120px)',
                borderRadius: '12px',
                overflow: 'hidden',
            },
        });

        if (!ref) {
            return EMPTY;
        }

        return ref.onChildComponentLoaded.pipe(
            tap((component) => {
                component.inputs = inputs;
            }),
            switchMap((component) =>
                race(
                    component.accepted.pipe(map((output) => ({ output }))),
                    component.cancelled.pipe(map(() => undefined)),
                    ref.onClose.pipe(map(() => undefined)),
                ),
            ),
            take(1),
            filter((result) => !!result),
            map((result) => result.output),
            finalize(() => {
                ref.close();
            }),
        );
    };
}
